import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Bell, CreditCard, Trash2, Save, Loader2, AlertCircle, CheckCircle, Coins } from 'lucide-react';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Toggle from '../../components/Toggle';
import LicenseUpgradeModal from '../../components/LicenseUpgradeModal';
import { useAuth } from '../../contexts/AuthContext';
import { useNotificationPreferences } from '../../hooks/useSupabase';
import { supabase } from '../../lib/supabase';

const Settings: React.FC = () => {
  const { user, profile: authProfile, updateProfile, loading: authLoading } = useAuth();
  const { preferences, loading: loadingPrefs, updatePreferences } = useNotificationPreferences();
  
  const [profile, setProfile] = useState<{ fullName: string; email: string; company: string; license?: string }>(
    { fullName: '', email: '', company: '', license: undefined }
  );

  const [notifications, setNotifications] = useState({
    emailUpdates: true,
    marketingEmails: false,
    productNews: true
  });

  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Load user data on mount
  useEffect(() => {
    if (user && authProfile) {
      setProfile({
        fullName: authProfile.full_name || '',
        email: user.email || '',
        company: authProfile.company_name || '',
        license: (authProfile as any).license
      });
    }
  }, [user, authProfile]);

  // Load notification preferences
  useEffect(() => {
    if (preferences) {
      setNotifications({
        emailUpdates: preferences.email_updates,
        marketingEmails: preferences.marketing_emails,
        productNews: preferences.product_news
      });
    }
  }, [preferences]);

  const handleSaveProfile = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      setMessage(null);

      // Update profile
      await updateProfile({
        full_name: profile.fullName,
        company_name: profile.company
      });

      // Update notification preferences
      await updatePreferences({
        email_updates: notifications.emailUpdates,
        marketing_emails: notifications.marketingEmails,
        product_news: notifications.productNews
      });

      // Update email if changed
      if (profile.email !== user.email) {
        const { error } = await supabase.auth.updateUser({
          email: profile.email
        });
        if (error) throw error;
        
        // Note: Email change requires confirmation, so the email won't change immediately
        setMessage({ 
          type: 'success', 
          text: 'Settings saved! Check your email to confirm the email address change.' 
        });
        return;
      }

      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      console.log('Settings saved successfully', { profile, notifications });
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    try {
      setMessage(null);
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setChangingPassword(false);
      console.log('Password changed successfully');
    } catch (error: any) {
      console.error('Error changing password:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone.'
    );
    
    if (!confirmed) return;

    const doubleConfirmed = window.confirm(
      'This will permanently delete all your data, mockups, and settings. Are you absolutely sure?'
    );
    
    if (!doubleConfirmed) return;

    try {
      // In a real app, you'd call a backend function to handle account deletion
      // For now, we'll just sign out
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: 'Failed to delete account' });
    }
  };

  if (authLoading || loadingPrefs) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-h2 font-heading text-text-dark mb-2">
          User Settings
        </h1>
        <p className="text-body text-body-grey">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="space-y-8">
        {/* Profile Information */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <User className="w-5 h-5 mr-2 text-brand-purple" />
            Profile Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Full Name
              </label>
              <Input
                value={profile.fullName}
                onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Company Name
              </label>
              <Input
                value={profile.company}
                onChange={(e) => setProfile({ ...profile, company: e.target.value })}
                placeholder="Enter your company name"
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Lock className="w-5 h-5 mr-2 text-brand-purple" />
            Security
          </h2>
          <div className="space-y-4">
            {!changingPassword ? (
              <>
                <Button 
                  variant="secondary" 
                  size="medium"
                  onClick={() => setChangingPassword(true)}
                >
                  Change Password
                </Button>
                <p className="text-sm text-body-grey">
                  Keep your account secure with a strong password
                </p>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-label font-body text-text-dark mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-label font-body text-text-dark mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="primary" 
                    size="small"
                    onClick={handleChangePassword}
                  >
                    Update Password
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="small"
                    onClick={() => {
                      setChangingPassword(false);
                      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Bell className="w-5 h-5 mr-2 text-brand-purple" />
            Notification Preferences
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-text-dark">Email Updates</p>
                <p className="text-sm text-body-grey">Receive updates about your mockups</p>
              </div>
              <Toggle
                checked={notifications.emailUpdates}
                onChange={(checked) => setNotifications({ ...notifications, emailUpdates: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-text-dark">Marketing Emails</p>
                <p className="text-sm text-body-grey">Receive tips and special offers</p>
              </div>
              <Toggle
                checked={notifications.marketingEmails}
                onChange={(checked) => setNotifications({ ...notifications, marketingEmails: checked })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-text-dark">Product News</p>
                <p className="text-sm text-body-grey">Updates about new features and templates</p>
              </div>
              <Toggle
                checked={notifications.productNews}
                onChange={(checked) => setNotifications({ ...notifications, productNews: checked })}
              />
            </div>
          </div>
        </div>

        {/* Billing */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <CreditCard className="w-5 h-5 mr-2 text-brand-purple" />
            Billing & Subscription
          </h2>
          <div className="space-y-4">
            {/* License Info */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-text-dark text-lg">
                    {authProfile?.license ? `${String(authProfile.license).replace(/_/g,' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} License` : 'Free License'}
                  </p>
                  <p className="text-sm text-body-grey">
                    Your current Active License
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  authProfile?.license === 'agency' ? 'bg-purple-100 text-purple-800' :
                  authProfile?.license === 'professional' ? 'bg-blue-100 text-blue-800' :
                  authProfile?.license === 'template_pack' ? 'bg-green-100 text-green-800' :
                  authProfile?.license === 'commercial' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {authProfile?.license === 'agency' ? 'Premium' :
                   authProfile?.license === 'professional' ? 'Pro' :
                   authProfile?.license === 'template_pack' ? 'Plus' :
                   authProfile?.license === 'commercial' ? 'Basic' :
                   'Starter'}
                </span>
              </div>
            </div>

            {/* Upgrade Progress */}
            {authProfile?.license !== 'agency' && (
              <div className="mt-3">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full px-4 py-2 bg-gradient-to-r from-brand-purple to-brand-blue text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
                >
                  Upgrade License
                </button>
                <div className="mt-2">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>License Progress</span>
                    <span>
                      {authProfile?.license === 'professional' ? '4/5' :
                       authProfile?.license === 'template_pack' ? '3/5' :
                       authProfile?.license === 'commercial' ? '2/5' :
                       '1/5'} Unlocked
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                      style={{ 
                        width: `${
                          authProfile?.license === 'professional' ? 80 :
                          authProfile?.license === 'template_pack' ? 60 :
                          authProfile?.license === 'commercial' ? 40 :
                          20
                        }%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Credits Usage */}
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-text-dark flex items-center">
                    <Coins className="w-4 h-4 mr-2 text-yellow-600" />
                    Credits Balance
                  </h3>
                  <span className="text-2xl font-bold text-brand-purple">
                    {(authProfile?.credits_balance ?? 0).toLocaleString()}
                  </span>
                </div>
                
                {/* Credits Bar */}
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-brand-purple to-brand-blue transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, ((authProfile?.credits_balance ?? 0) / (
                          authProfile?.license === 'agency' ? 1100 :
                          authProfile?.license === 'professional' ? 725 :
                          authProfile?.license === 'template_pack' ? 500 :
                          authProfile?.license === 'commercial' ? 275 :
                          10
                        )) * 100)}%` 
                      }}
                    />
                  </div>
                  {/* Percentage indicator */}
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      Credits available for mockup generation
                    </p>
                    <p className="text-xs font-medium text-gray-700">
                      {Math.round(((authProfile?.credits_balance ?? 0) / (
                        authProfile?.license === 'agency' ? 1100 :
                        authProfile?.license === 'professional' ? 725 :
                        authProfile?.license === 'template_pack' ? 500 :
                        authProfile?.license === 'commercial' ? 275 :
                        10
                      )) * 100)}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Credits Stats */}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                <div>
                  <p className="text-sm text-gray-600">Generation Credits</p>
                  <p className="font-semibold text-gray-900">
                    {authProfile?.license === 'agency' ? '1,100' :
                     authProfile?.license === 'professional' ? '725' :
                     authProfile?.license === 'template_pack' ? '500' :
                     authProfile?.license === 'commercial' ? '275' :
                     '10'} credits
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Mockups This Month</p>
                  <p className="font-semibold text-gray-900">
                    {authProfile?.mockups_created_this_month || 0}
                  </p>
                </div>
              </div>

              {/* Low Credits Warning */}
              {authProfile?.credits_balance !== undefined && authProfile.credits_balance < ((
                authProfile?.license === 'agency' ? 1100 :
                authProfile?.license === 'professional' ? 725 :
                authProfile?.license === 'template_pack' ? 500 :
                authProfile?.license === 'commercial' ? 275 :
                10
              ) * 0.2) && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mr-2 flex-shrink-0" />
                  <p className="text-xs text-yellow-800">
                    Your credits are running low. Contact support to purchase additional credits.
                  </p>
                </div>
              )}
            </div>


          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800' 
              : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            {message.text}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-between items-center">
          <button 
            onClick={handleDeleteAccount}
            className="text-red-600 hover:text-red-700 font-medium flex items-center"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Delete Account
          </button>
          <Button 
            variant="primary" 
            size="large"
            onClick={handleSaveProfile}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* License Upgrade Modal */}
      <LicenseUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentLicense={authProfile?.license}
      />
    </div>
  );
};

export default Settings;
